import social, { SUCCESS } from "@/api/social";
import ConfirmationPopUp from "@/components/ConfirmationPopup";
import DailyHeadlineCommentPopUp from "@/components/editorial/DailyHeadlineCommentPopup";
import DailyHeadlineSubmission from "@/components/editorial/DailyHeadlineSubmission";
import type { DailyHeadline, DBAccount } from "@/local-shared/model";
import type { DailyHeadlineSubmissionPost } from "@/local-shared/returnTypes";
import { AuthContext } from "@/Realize";
import { useContext, useState } from "react";
import HTTPStatus from "@/realize-shared/HTTPStatus";
import { AnimatePresence } from "framer-motion";

function NavigationElement({
    onBack,
    headline,
    profile,
    setShowConfirmationDialog,
}: {
    onBack: () => void;
    headline: DailyHeadline;
    profile: DBAccount;
    setShowConfirmationDialog: (show: boolean) => void;
})
{
    const userData = useContext(AuthContext);

    const extraTools = () =>
    {
        if (userData?.id.toString() === profile.id.toString())
        {
            return (
                <div>
                    {!headline.closed && (
                        <button
                            className={Wind.style({
                                height: "h-8",
                                display: "flex",
                                alignItems: "items-center",
                                justifyContent: "justify-center",
                                borderRadius: "rounded-full",
                                backgroundColor: "bg-sharp-red",
                                color: "text-white",
                                paddingX: "px-4",
                            }).class}
                            aria-label="Close Submissions"

                            onClick={() =>
                            {
                                setShowConfirmationDialog(true);
                            }}
                        >
                            Close Submissions
                        </button>
                    )}
                </div>
            );
        }

        return null;
    };

    return (
        <div
            className={Wind.style({
                paddingY: "py-2",
                paddingX: "px-4",
                display: "flex",
                alignItems: "items-center",
                justifyContent: "justify-between",
            }).class}
        >
            <button
                className={Wind.style({
                    width: "w-8",
                    height: "h-8",
                    display: "flex",
                    alignItems: "items-center",
                    justifyContent: "justify-center",
                    borderRadius: "rounded-full",
                    borderColor: "border-[#524435]",
                    borderWidth: "border",
                }).class}
                onClick={onBack}
                aria-label="Back"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        // eslint-disable-next-line @stylistic/max-len
                        d="M13.7467 4.37479L8.12172 9.99979L13.7467 15.6248L11.9789 17.3926L4.58618 9.99979L11.9789 2.60703L13.7467 4.37479Z"
                        fill="#524435"
                    />
                </svg>
            </button>
            {extraTools()}
        </div>
    );
}

export default function ProfileHeadlinesView({
    headline,
    submissions,
    profile,
    resetSubmissions,
}: {
    headline: DailyHeadline;
    submissions: DailyHeadlineSubmissionPost[];
    profile: DBAccount;
    resetSubmissions: (reload?: boolean) => void;
})
{
    const [viewingComments, setViewingComments] = useState<DailyHeadlineSubmissionPost | null>(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<number|null>(null);

    return (
        <>
            <div
                className={Wind.style({
                    flexGrow: "grow",
                    height: "h-full",
                    display: "flex",
                    flexDirection: "flex-col",
                }).class}
            >
                <NavigationElement
                    onBack={resetSubmissions}
                    profile={profile}
                    headline={headline}
                    setShowConfirmationDialog={setShowConfirmationDialog}
                />
                <div
                    className={Wind.style({
                    // Overscroll Behavior; Mandatory y snapping
                        overscrollBehavior: "overscroll-y-contain",
                        flexGrow: "grow",
                        overflowY: "overflow-y-auto",
                        overflowX: "overflow-x-hidden",
                        paddingX: "px-4",
                    }).class}
                    style={{
                        scrollSnapType: "y mandatory",
                    }}
                >
                    {submissions.map((post) =>
                        (
                            <DailyHeadlineSubmission

                                key={post.id}
                                canOpenFullscreen={viewingComments === null}
                                updatePost={(_post) =>
                                {
                                    // setPosts(submissions.map(p => p.id === post.id ? post : p));
                                }}
                                post={post as DailyHeadlineSubmissionPost & { headline: string }}
                                onShowComments={() =>
                                {
                                    setViewingComments(post);
                                }}
                                setShareValue={(_value) =>
                                {
                                    // setShareValue(value);
                                }}
                                bannerText="Chosen Winner!"
                                isWinner={headline.winning_post_id === post.id}
                                pastHeadline={headline.headline}
                                resetSubmissions={resetSubmissions}
                                onWinnerSelected={(winnerID: number) =>
                                {
                                    // TODO: Handle winner selection
                                    Logger.log("Winner selected:", winnerID);

                                    social.chooseHeadlineWinner(winnerID, headline.id).then((results) =>
                                    {
                                        if (!results)
                                        {
                                            Logger.error("Failed to select winner: No results returned");

                                            return;
                                        }

                                        if (results.status as HTTPStatus !== SUCCESS)
                                        {
                                            Logger.error("Failed to select winner:", results.message);

                                            return;
                                        }

                                        Logger.info("Successfully selected winner:", results.message);
                                        resetSubmissions();
                                    })
                                        .catch((error) =>
                                        {
                                            Logger.error("Failed to select winner:", error);
                                        });
                                }}

                                onDeleteSubmission={setShowDeleteConfirmation}

                                headline={headline}
                            />
                        ))}
                </div>
            </div>
            <AnimatePresence>
                {showDeleteConfirmation && (
                    <ConfirmationPopUp
                        title="Delete Submission"
                        confirmText="Delete"
                        showCancelButton
                        onConfirm={() =>
                        {
                            Logger.log("Deleting submission with ID:", showDeleteConfirmation);

                            social.profile.deleteSubmission(showDeleteConfirmation)
                                .then((results) =>
                                {
                                    if (!results)
                                    {
                                        Logger.error("Failed to delete submission: No results returned");

                                        return;
                                    }

                                    setShowDeleteConfirmation(null);
                                    resetSubmissions(true);
                                })
                                .catch((error) =>
                                {
                                    Logger.error("Failed to delete submission:", error);
                                    setShowDeleteConfirmation(null);
                                });
                        }}
                        onClose={() =>
                        {
                            setShowDeleteConfirmation(null);
                        }}
                    >
                        Are you sure you want to delete this submission?
                        <div className="font-bold underline italic">
                            This action cannot be undone.
                        </div>
                        <strong>All comments and votes associated with this submission will also be deleted.</strong>
                    </ConfirmationPopUp>
                )}
                {showConfirmationDialog && (
                    <ConfirmationPopUp
                        title="Close Submissions"
                        confirmText="Yes"
                        showCancelButton
                        onClose={() =>
                        {
                            setShowConfirmationDialog(false);
                        }}
                        onConfirm={() =>
                        {
                            social.closeDailyHeadline(headline.id)
                                .then((results) =>
                                {
                                    if (!results)
                                    {
                                        Logger.error("Failed to close headline: No results returned");

                                        return;
                                    }

                                    if (results.status as HTTPStatus !== SUCCESS)
                                    {
                                        Logger.error("Failed to close headline:", results.message);

                                        return;
                                    }

                                    Logger.info("Successfully closed headline:", results.message);
                                    setShowConfirmationDialog(false);
                                    resetSubmissions();
                                })
                                .catch((error) =>
                                {
                                    Logger.error("Failed to close headline:", error);
                                    setShowConfirmationDialog(false);
                                });
                        }}
                    >
                        Please note that closing submissions will prevent any further submissions to this headline.
                        <div className="font-bold">
                            Are you sure you want to close submissions?
                        </div>
                        <div>
                            <i>This action cannot be undone.</i>
                        </div>
                    </ConfirmationPopUp>
                )}
            </AnimatePresence>
            <DailyHeadlineCommentPopUp
                viewingComments={viewingComments}
                setViewingComments={setViewingComments}
                setError={(_error) =>
                {
                    // TODO: Handle error state
                }}
            />
        </>
    );
}
